import { Component, Show, createSignal, onMount } from "solid-js";
import { Button, Spinner } from "../ui";
import { QuickCreateCard } from "../board/QuickCreateCard";
import { api } from "../../api/client";
import type {
  ProjectId,
  WorkItemType,
  WorkItemStatus,
  WorkItem,
  CreateWorkItemRequest,
} from "../../api/types";

export interface GuidedCreateStepProps {
  projectId: ProjectId;
  onComplete: () => void;
  onSkip: () => void;
}

/**
 * GuidedCreateStep - Onboarding step that walks users through creating
 * their first work item with callouts highlighting AI enrichment features.
 */
export const GuidedCreateStep: Component<GuidedCreateStepProps> = (props) => {
  const [itemTypes, setItemTypes] = createSignal<WorkItemType[]>([]);
  const [defaultStatusId, setDefaultStatusId] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [creating, setCreating] = createSignal(false);
  const [error, setError] = createSignal("");
  const [created, setCreated] = createSignal(false);
  const [createdItemTitle, setCreatedItemTitle] = createSignal("");

  onMount(async () => {
    try {
      const [types, statuses] = await Promise.all([
        api.get<WorkItemType[]>(
          `/projects/${props.projectId}/work-items/types`,
        ),
        api.get<WorkItemStatus[]>(`/projects/${props.projectId}/statuses`),
      ]);
      setItemTypes(types);

      const defaultStatus =
        statuses.find((s) => s.is_default) ??
        statuses.find((s) => s.category === "todo") ??
        statuses[0];
      if (defaultStatus) {
        setDefaultStatusId(defaultStatus.id);
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load project settings",
      );
    } finally {
      setLoading(false);
    }
  });

  const handleCreate = async (data: CreateWorkItemRequest) => {
    setCreating(true);
    setError("");
    try {
      await api.post<WorkItem>(`/projects/${props.projectId}/work-items`, data);
      setCreatedItemTitle(data.title);
      setCreated(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create work item");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div class="h-full flex items-center justify-center p-8">
      {/* Success celebration state */}
      <Show when={created()}>
        <div class="max-w-md text-center">
          <div class="w-16 h-16 bg-signal-green-tint rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              class="w-8 h-8 text-signal-green"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 class="text-2xl font-bold text-ink mb-2">You're all set!</h1>
          <p class="text-ink-2 mb-2">
            <span class="font-medium text-ink">"{createdItemTitle()}"</span> is
            on your board. Next: describe something bigger and let Plan break it
            down.
          </p>
          <p class="text-sm text-ink-3 mb-8">
            As you add more items, the AI will learn your project's patterns and
            provide even better suggestions.
          </p>
          <Button variant="primary" size="lg" onClick={props.onComplete}>
            Start planning
          </Button>
        </div>
      </Show>

      {/* Create form with guided annotations */}
      <Show when={!created()}>
        <div class="w-full max-w-xl">
          <div class="text-center mb-6">
            <div class="inline-flex items-center gap-1.5 bg-sunken text-ink-2 border border-line text-xs font-medium px-2.5 py-1 rounded-full mb-3">
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              Final step
            </div>
            <h1 class="text-2xl font-bold text-ink mb-2">
              Create your first work item
            </h1>
            <p class="text-ink-2">
              Type a title below and watch the AI classify it, suggest labels,
              and enrich your item in real time.
            </p>
          </div>

          {/* Loading state */}
          <Show when={loading()}>
            <div class="flex items-center justify-center py-12">
              <Spinner size="lg" class="text-ink" />
            </div>
          </Show>

          {/* Error state */}
          <Show when={!loading() && error() && !creating()}>
            <div class="bg-signal-red-tint border border-signal-red/25 rounded p-4 mb-4 text-sm text-signal-red">
              {error()}
            </div>
          </Show>

          {/* Main create form with annotation tips */}
          <Show
            when={!loading() && itemTypes().length > 0 && defaultStatusId()}
          >
            {/* AI feature tips */}
            <div class="bg-signal-magenta-tint border border-signal-magenta/25 rounded p-4 mb-4">
              <div class="flex items-start gap-3">
                <div class="w-8 h-8 bg-surface rounded border border-line flex items-center justify-center flex-shrink-0">
                  <svg
                    class="w-4 h-4 text-signal-magenta"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <div class="text-sm">
                  <p class="font-medium text-signal-magenta mb-1">
                    AI-powered enrichment
                  </p>
                  <ul class="text-ink-2 space-y-0.5">
                    <li>
                      Type 3+ characters and the AI will{" "}
                      <span class="font-medium">auto-classify</span> your item
                      type
                    </li>
                    <li>
                      It suggests{" "}
                      <span class="font-medium">
                        labels, priority, and subtasks
                      </span>{" "}
                      based on your title
                    </li>
                    <li>
                      Try something like{" "}
                      <span class="font-medium italic">
                        "Fix login button not responding on mobile"
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Work item type guide */}
            <div class="grid grid-cols-4 gap-2 mb-4">
              <TypeHint
                name="Epic"
                color="bg-signal-cyan-tint text-signal-cyan"
                desc="Big initiative"
              />
              <TypeHint
                name="Story"
                color="bg-signal-green-tint text-signal-green"
                desc="User-facing value"
              />
              <TypeHint
                name="Task"
                color="bg-signal-blue-tint text-signal-blue"
                desc="Unit of work"
              />
              <TypeHint
                name="Bug"
                color="bg-signal-red-tint text-signal-red"
                desc="Something broken"
              />
            </div>

            {/* Embedded QuickCreateCard */}
            <div class="bg-surface rounded border border-line p-4">
              <QuickCreateCard
                projectId={props.projectId}
                statusId={defaultStatusId()}
                itemTypes={itemTypes()}
                loading={creating()}
                onCreate={handleCreate}
                onCancel={() => {}}
              />
            </div>
          </Show>

          {/* Skip link */}
          <div class="text-center mt-6">
            <button
              type="button"
              onClick={props.onSkip}
              class="text-sm text-ink-3 hover:text-ink-2 underline underline-offset-2"
            >
              Skip for now — I'll explore first
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};

/** Small type hint pill for the onboarding guide */
const TypeHint: Component<{
  name: string;
  color: string;
  desc: string;
}> = (props) => (
  <div class={`rounded-md px-2.5 py-1.5 text-center ${props.color}`}>
    <div class="text-xs font-semibold">{props.name}</div>
    <div class="text-[10px] opacity-75">{props.desc}</div>
  </div>
);

export default GuidedCreateStep;
